import React, { useState } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import MenuIcon from "@mui/icons-material/Menu";
import * as constants from "./utils/constants";
import Footer from "./Footer";
import { theme } from "./utils/theme";
import ProductDetails from "./ProductDetails";
import AboutUsPage from "./AboutUs";
import HomePage from "./HomePage";
import FAQsPage from "./FaqsPage";
import ContactUsPage from "./ContactUs";
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from "react-router-dom";

// --- Animated Background ---

function AnimatedBackground() {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        overflow: "hidden",
        background: `linear-gradient(to right, #0f2027, #203a43, #2c5364)`,
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          backgroundColor: "rgba(58, 123, 213, 0.3)",
          filter: "blur(120px)",
        }}
        animate={{
          x: ["-15vw", "95vw", "-15vw"],
          y: ["5vh", "85vh", "5vh"],
        }}
        transition={{
          duration: 22,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "mirror",
        }}
      />
      <motion.div
        style={{
          position: "absolute",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          backgroundColor: "rgba(0, 210, 255, 0.3)",
          filter: "blur(120px)",
        }}
        animate={{
          x: ["85vw", "15vw", "85vw"],
          y: ["75vh", "15vh", "75vh"],
        }}
        transition={{
          duration: 28,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "mirror",
          delay: 7,
        }}
      />
    </Box>
  );
}

// --- Main App ---

export default function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = false;
  const navigate = useNavigate()

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setCurrentPage("productDetails");
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setSelectedProduct(null);
    setCurrentPage("home");
    window.scrollTo(0, 0);
  };

  const handleNavClick = (page) => {
    navigate(page)
  };

  const navItems = [
    { label: "Home", page: "home", redirect: "/" },
    { label: "About Us", page: "about", redirect: "/about-us" },
    { label: "FAQs", page: "faqs", redirect: "/faqs-page" },
    { label: "Contact Us", page: "contact", redirect: "/contact-us" },
  ];

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "center",
        background: theme.palette.background.default,
        height: "100%",
      }}
    >
      <Typography variant="h6" sx={{ my: 2 }}>
        {constants.BRAND_NAME}
      </Typography>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} alignContent="center">
            <Button
              variant="outlined"
              onClick={() => handleNavClick(item.redirect)}
              sx={{
                textAlign: "center",
                backgroundColor: "transparent",
                width: "90%",
                my: 1,
                fontSize: "20px",
              }}
            >
              {item.label}
            </Button>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderContent = () => {
    // Riddhi Siddhi Flour Mill
    // Ridhi Sidhi Flour Mill
    // Domestic
    // Mill
    // Flour
    // Using a stable key for ProductDetails prevents re-mounts that cause scroll jumps
    switch (currentPage) {
      case "home":
        return <HomePage key="home" onSelectProduct={handleSelectProduct} />;
      case "about":
        return <AboutUsPage key="about" />;
      case "faqs":
        return <FAQsPage key="faqs" />;
      case "contact":
        return <ContactUsPage key="contact" />;
      case "productDetails":
        return selectedProduct ? (
          <ProductDetails
            key={selectedProduct.id}
            product={selectedProduct}
            onBack={handleBackToHome}
          />
        ) : (
          <HomePage key="home_fallback" onSelectProduct={handleSelectProduct} />
        );
      default:
        return <HomePage key="default" onSelectProduct={handleSelectProduct} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* <Header> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "95vh",
        }}
      >
      <CssBaseline />
      {/* <AnimatedBackground /> */}
        <AppBar
          component="nav"
          sx={{
            pl: { lg: "1vw", sm: "0" },
            pr: { lg: "2vw", sm: "0" },
            background: "#ededf7",
          }}
        >
          <Toolbar>
            <Box
              sx={{
                display: "flex",
                flexGrow: 1,
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              {isMobile && (
                <IconButton
                  style={{ color: "#131310" }}
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography
                variant="h4"
                component="div"
                sx={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontFamily: "Times New Roman",
                  verticalAlign: "center",
                }}
                // , fontFamily:'Lora, serif-fonts'
                onClick={() => handleNavClick("/")}
              >
                {constants.BRAND_NAME}
              </Typography>
              <Box>
                {!isMobile && (
                  <Box sx={{}}>
                    {navItems.map((item) => (
                      <Button
                        sx={{ backgroundColor: "transparent" }}
                        key={item.label}
                        onClick={() => handleNavClick(item.redirect)}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <nav>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            anchor="right"
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 240,
                background: theme.palette.background.default,
              },
            }}
          >
            {drawer}
          </Drawer>
        </nav>
        <Box component="main" sx={{ flexGrow: 1 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <Routes>
                <Route path="/" element={<HomePage onSelectProduct={handleSelectProduct} />} />
                <Route path="/about-us" element={<AboutUsPage />} />
                <Route path="/faqs-page" element={<FAQsPage />} />
                <Route path="/contact-us" element={<ContactUsPage/>}/>
              </Routes>
              {/* {renderContent()} */}
            </motion.div>
          </AnimatePresence>
        </Box>
        <Footer />
      </Box>
      {/* </Header> */}
    </ThemeProvider>
  );
}
